import { useEffect, useState, Fragment, useMemo } from "react";
import { Typography, Button } from "@mui/material";
import { tss } from "tss-react/mui";
import { Scrollbars } from "react-custom-scrollbars";
import { panelHeight, panelRight } from "constants/ui";
import * as Utils from "services/UtilsService";
import { getCachedData } from "api/Cache";
import ApiUtils from "api/Utils";
import { updateData } from "services/CacheService";
import { PanelHeader } from "components";
import AuthService from "services/AuthService";
import ViewerEvents from "events/ViewerEvents";
import { Room, Send } from "@mui/icons-material";
import IconComments from "assets/images/ic_comments.svg";
import commonStyles, { backgroundColor } from "styles/common";
import { alpha } from '@mui/material/styles';
import {  useSelector } from 'react-redux';

const useStyles = tss.create(({theme}) => ({
    root: {
        position: "absolute",
        display: "table",
        background: alpha(theme.palette.background.default, 0.85), //"white",
        color: theme.palette.text.primary,
        top: panelHeight,
        right: panelRight.comments,
        width: 300,
        height: "calc(100vh - 109px)",
        userSelect: "none",
        zIndex: 1,
    },
    header: {
        position: "relative",
        display: "block",
        width: "100%",
        height: panelHeight,
    },
    drag: {
        height: 32,
        width: 284,
        backgroundColor: alpha(theme.palette.primary.main, 0.5),//"#99C8DF",
        position: "relative",
        left: 8,
        borderRadius: 4,
        cursor: "move",
    },
    dragIcon: {
        position: "absolute",
        zIndex: 10000000,
        display: "none",
    },
    dragLeft: {
        height: 32,
        width: 64,
        borderRadius: "4px 4px 4px 0",
    },
    dragRight: {
        backgroundColor: theme.palette.primary.main, //"#0075B0",
        height: 32,
        width: 220,
        borderRadius: "0 4px 4px 0",
    },
    dragRightLabel: {
        fontWeight: "bold",
        fontSize: 12,
        color: "#FFFFFF",
    },
}));

const useStylesComment = tss.create(({theme}) => ({
    root: {
        width: 284,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        borderRadius: 4,
        left: 8,
        marginTop: 10,
    },
    container: {
        width: 256,
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
        position: "relative",
        height: "100%",
        borderRadius: 4,
        padding: 8
    },
    row: {
        display: "flex",
        alignItems: "center",
        minHeight: 24,
    },
    sender: {
        display: "inline-block",
        fontWeight: "bold",
        fontSize: 14,
        width: 107,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        verticalAlign: "middle",
        paddingRight: 5,
        lineHeight: "16px",
        height: 16,
    },
    date: {
        color: theme.palette.text.disabled,
        fontSize: 12,
        lineHeight: "14px",
        marginTop: -10,
    },
}));

const useStylesThread = tss.create(({theme}) => ({
    rootThread: {
        width: 284,
        backgroundColor: theme.palette.background.paper, //"#F4F4F4",
        position: "relative",
        marginTop: 10,
        left: 8,
        overflow: "auto",
        borderRadius: 4,
    },
    textArea: {
        position: "relative",
        resize: "none",
        backgroundColor: alpha(theme.palette.background.paper, 0.5), //"#F4F4F4",
        fontSize: 14,
        color: theme.palette.text.secondary, //"rgba(0,0,0,0.55)",
        marginBottom: 10,
        width: 260,
    },
    commentHeader: {
        marginTop: 20,
    },
    button: {
        float: "right",
        right: 10,
        width: 80,
        marginBottom: 10,
        fontWeight: "bold",
    },
}));

const json: any = {
    type: "FeatureCollection",
    name: "test-points-short-named",
    crs: {
        type: "name",
        properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" },
    },
    features: [
        //{ "type": "Feature", "properties": { "name": "1" }, "geometry": { "type": "Point", "coordinates": [ 0.0, 0.0, 0.0 ] } },
    ],
};

function moveAt(left: any, top: any) {
    const icon: any = document.getElementById("draggedIcon");
    //console.log("left:", left, "|top:", top);
    if (left > 1 && top > 1) {
        icon.style.left = left - icon.offsetWidth / 2 + "px";
        icon.style.top = top - icon.offsetHeight / 2 + "px";
        icon.style.display = "inline-block";
    }
}

function onMouseMove(event: any) {
    const icon: any = document.getElementById("draggedIcon");
    if (icon && icon !== null) {
        //console.log("event.target.tagName:", event);
        if (event.target.tagName === "CANVAS" || event.target.tagName === "IMG") {
            moveAt(event.pageX, event.pageY);
        } else {
            icon.style.display = "none";
        }
    }
}

function onMouseUp(event: any, callback: any) {
    const icon: any = document.getElementById("draggedIcon");
    if (icon && icon !== null) {
        const element = event.target;
        if (element.tagName === "CANVAS" || element.tagName === "IMG") {
            callback(event);
        }
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseMove);
        icon.style.display = "none";
    }
}

function onMouseDown(event: any, callback: any) {
    const icon: any = document.getElementById("draggedIcon");
    if (icon && icon !== null) {
        event.preventDefault();
        moveAt(event.pageX, event.pageY);
        document.addEventListener("mousemove", onMouseMove, false);
        document.addEventListener(
            "mouseup",
            (event) => {
                onMouseUp(event, callback);
            },
            false
        );
    }
}

function handleCache(viewId: string, comment: any) {
    var cachedThreads: any = getCachedData("comments")[viewId] || [];
    cachedThreads.push(comment);
    updateData("save", "comments", viewId, cachedThreads);
    ViewerEvents.emit("updateComments", cachedThreads);
}

function handleRemote(commentId: string, thread: any, done: (comment: any) => void) {
    ApiUtils.serverCreateId((id: string) => {
        var comment: any = {};
        comment["ID"] = id;
        comment["project"] = commentId;
        comment["type"] = "COMMENT";
        comment["x"] = thread.x;
        comment["y"] = thread.y;
        comment["z"] = thread.z;
        comment["thread_id"] = thread.thread_id;
        comment["date"] = thread.date;
        comment["sender"] = thread.sender;
        comment["content"] = thread.content;

        ApiUtils.remoteUpdateEntity(commentId, id, comment, function (response: any) {
            if (response.result === "success") {
                done(comment);
            }
        });
    });
}
/*
function hideGeoJSONLayer(viewer: any, commentId: string) {
    viewer.getViewer().hideGeoJSONLayer(commentId);
}
*/
function loadGeoJSONLayer(viewer: any, commentId: string, jsonInstance: any) {
    console.log("jsonInstance:", jsonInstance);
    viewer.getViewer().loadGeoJSONLayer(commentId, jsonInstance, true);
}

function unloadGeoJSONLayer(viewer: any, commentId: string) {
    viewer.getViewer().unloadGeoJSONLayer(commentId);
}

function updateViewerPoints(thread: any, jsonInstance: any, callback: any) {
    const features: any = jsonInstance.features;
    features.push({
        id: thread["thread_id"],
        type: "Feature",
        properties: { name: thread.content },
        geometry: {
            type: "Point",
            coordinates: [thread.x, thread.y, thread.z],
        },
    });
    if (callback) {
        callback(jsonInstance);
    }
}

function Thread(props: { view: any; viewer: any; index: number; thread: any; handlePending: any }) {
    const { classes, cx } = useStylesThread();
    const { classes: common } = commonStyles();
    const { view, viewer, index, thread, handlePending } = props;
    const [value, setValue] = useState("");
    const [showError, setShowError] = useState(false);
    const [hasFullAccess, setHasFullAccess] = useState(false);
	const storeAuthData = useSelector((state: any) => state.auth);
    const { isDefaultProject = false, projectAccessLevel:access_level = "read" } = storeAuthData;
    
    useEffect(() => {
        setHasFullAccess(access_level === 'full' && !isDefaultProject)
    }, [access_level, isDefaultProject])

    function handleChange(event: any) {
        setValue(event.target.value);
    }

    function handleClick() {
        setValue("");
        setShowError(false);
    }

    const sendComment = () => {
        if (value.length === 0) {
            setShowError(true);
            return;
        }
        const currentUser: string = AuthService.getUserName();
        var commentEntry: any = {
            sender: currentUser,
            date: new Date().getTime() + "",
            content: value,
        };
        var selectedView = view.ID;
        var commentId: string = "comments:" + selectedView;
        var threadEntry = {
            ...commentEntry,
            x: thread.x,
            y: thread.y,
            z: thread.z,
            thread_id: thread.thread_id,
        };

        handleRemote(commentId, threadEntry, (comment: any) => {
            unloadGeoJSONLayer(viewer, commentId);
            setValue("");
            handleCache(selectedView, comment);
            handlePending(index, thread);
        });
    };

    const findComment = () => {
        console.log("findComment.thread:", thread);
        viewer.frameWorldBounds([
            [parseFloat(thread.x) - 50.0, parseFloat(thread.y) - 50.0, parseFloat(thread.z) - 50.0],
            [parseFloat(thread.x) + 50.0, parseFloat(thread.y) + 50.0, parseFloat(thread.z) + 50.0],
        ]);
    };

    return (
        <div className={cx(classes.rootThread, common.sectionContent)}>
            <div className={classes.commentHeader}>
                <textarea
                    id="comment"
                    className={classes.textArea}
                    rows={4}
                    cols={33}
                    title="Add a new comment"
                    value={value}
                    onChange={handleChange}
                    onClick={handleClick}
                />
            </div>
            <div style={{ display: showError ? "block" : "none" }}>
                <Typography className={common.mediumLabel} style={{ color: "red" }}>
                    Comments can not be empty!
                </Typography>
            </div>
            <Button className={classes.button} onClick={sendComment} color="primary" disabled={!hasFullAccess}>
                <Send /> Send
            </Button>
            <Button className={classes.button} onClick={findComment} color="primary" disabled={!hasFullAccess}>
                <Room /> Find
            </Button>
        </div>
    );
}

function CommentEntry(props: { comment: any }) {
    const { classes } = useStylesComment();
    const { classes: common } = commonStyles();
    const { comment } = props;
    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <div className={classes.row}>
                    <Typography className={classes.sender}>{comment.sender}</Typography>
                </div>
                <div className={classes.row}>
                    <Typography className={classes.date}>
                        {comment.date ? Utils.formatDateDetailed(comment.date) : ""}
                    </Typography>
                </div>
                <div className={classes.row}>
                    <Typography className={common.mediumLabel}>{comment.content}</Typography>
                </div>
            </div>
        </div>
    );
}

type ICommentsProps = {
    newScene: boolean;
    handleClose: (name: string) => void;
};

export default function Comments(props: ICommentsProps) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { viewer, view } = ViewerEvents;
    var comments: any = getCachedData("comments");
    var commentId: string = "-1";
    if (view && view !== null) {
        comments = Object.values(getCachedData("comments")[view.ID]) || [];
        comments = Utils.sortArrayByProp(comments, "date", true, true);
        commentId = "comment:" + view["ID"];
    }
    const [pending, setPending] = useState<any>([]);
    const [points, setPoints] = useState<any>([]);
    const [update, setUpdate] = useState(true);
    const jsonInstance = Utils.deepCopyObject(json);
    let totalThreads = useMemo(() => [...comments, ...pending], [comments, pending]);
    totalThreads = Utils.sortArrayByPropValue(totalThreads, "status", "pending", true);
    console.log("comments:", comments);
    console.log("totalThreads:", totalThreads);

    useEffect(() => {
        if (viewer && update) {
            Object.values(totalThreads).forEach((thread: any) => {
                updateViewerPoints(thread, jsonInstance, (newJsonInstance: any) => {
                    loadGeoJSONLayer(viewer, commentId, newJsonInstance);
                });
            });
            setUpdate(false);
        }
    }, [totalThreads, update, commentId, jsonInstance, viewer]);

    const handleClose = () => {
        props.handleClose("comments");
    };

    const addThread = (point: any) => {
        ApiUtils.serverCreateId((id) => {
            const newThread = {
                x: point.x,
                y: point.y,
                z: point.z,
                thread_id: id,
                content: "new comment",
                status: "pending",
            };
            const newPoints: any = [...points];
            newPoints.push(point);
            setPoints(newPoints);
            const curPendingThreads: any = [...pending];
            curPendingThreads.push(newThread);
            setPending(curPendingThreads);
            setUpdate(true);
        });
    };

    const handlePending = (index: number) => {
        //console.log("pending:", pending);
        const newPending = [...pending];
        newPending.splice(index, 1);
        //console.log("newPending:", newPending);
        setPending(newPending);
        setUpdate(true);
    };

    const handleMouseDown = (event: any) => {
        var raycastViewResult = viewer.raycastViewPrimitives(event.pageX, event.pageY);
        if (raycastViewResult && raycastViewResult.worldPoint && raycastViewResult.valid) {
            var point = {
                x: raycastViewResult.worldPoint[0],
                y: raycastViewResult.worldPoint[1],
                z: raycastViewResult.worldPoint[2],
            };
            addThread(point);
        }
    };
    return (
        <Fragment>
            {viewer && viewer !== null ? (
                <Fragment>
                    <div className={classes.root}>
                        <div className={classes.header}>
                            <PanelHeader title="Comments" handleClose={handleClose} />
                            <div className={cx(common.middle, classes.drag)}>
                                <div className={cx(common.middleCenter, classes.dragLeft)}>
                                    <img
                                        id="draggedImg"
                                        alt="comment"
                                        src={IconComments}
                                        onMouseDown={(event: any) => {
                                            onMouseDown(event, handleMouseDown);
                                        }}
                                    />
                                </div>
                                <div className={cx(common.middleCenter, classes.dragRight)}>
                                    <Typography className={classes.dragRightLabel}>
                                        Drag to a point to comment
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        <div style={{ paddingTop: 27 }}>
                            <Scrollbars
                                autoHeight
                                autoHeightMax={"calc(100vh - 210px)"}
                                autoHide
                                autoHideTimeout={500}
                                autoHideDuration={200}
                                style={{ overflowX: "hidden" }}
                            >
                                {totalThreads.map((thread: any, index: number) =>
                                    thread["status"] === "pending" ? (
                                        <Thread
                                            key={"thread-" + index}
                                            index={index}
                                            view={view}
                                            viewer={viewer}
                                            thread={thread}
                                            //jsonInstance={jsonInstance}
                                            handlePending={handlePending}
                                        />
                                    ) : (
                                        <CommentEntry key={"comment-" + index} comment={thread} />
                                    )
                                )}
                            </Scrollbars>
                        </div>
                    </div>
                    <img
                        id="draggedIcon"
                        className={classes.dragIcon}
                        alt={"drag Icon"}
                        src={IconComments}
                        draggable={true}
                    />
                </Fragment>
            ) : (
                ""
            )}
        </Fragment>
    );
}
